import React, { useEffect, useRef, useState } from 'react';
import { Coins } from 'lucide-react';
import { Tooltip } from '../ui';
import { OrgCredits } from '../../client/creditsClient';
import { CreditRequestState } from '../../hooks/useCreditRequest';
import CreditsPopover from './CreditsPopover';
import { formatCredits } from '../../utils/credits';

/**
 * Org credit balance in the sidebar footer, above the user card. Presentational
 * — the caller supplies the fetched state (see `useOrgCredits`). Neutral navy
 * chrome styling: credits are an account fact, never an AI surface, so violet
 * stays out of this component.
 */

/**
 * Below this the chip turns warn-toned: the balance no longer covers the
 * CHEAPEST metered action. OUTREACH_MESSAGE is seeded at 5 credits/message
 * (SUPPLIER_RESEARCH costs far more), and rates are admin-editable, so this is
 * a display heuristic — it can move to the live meter config once members are
 * allowed to read one.
 */
export const LOW_BALANCE_THRESHOLD = 5;

const chipBase =
  'flex w-full items-center gap-s3 rounded-control border px-s3 py-[7px] text-left text-[12.5px]';

export interface CreditsChipProps {
  credits: OrgCredits | null;
  loading?: boolean;
  /** Credits API did not answer — the chip shows an honest em dash. */
  unreachable?: boolean;
  /** The popover's "Request more credits" state (see `useCreditRequest`); absent, the popover is history only. */
  creditRequest?: CreditRequestState;
  /** Popover open on mount (design gallery / tests only). */
  defaultOpen?: boolean;
  className?: string;
}

export const CreditsChip: React.FC<CreditsChipProps> = ({
  credits,
  loading = false,
  unreachable = false,
  creditRequest,
  defaultOpen = false,
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  const glyph = <Coins className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />;

  // No number to show: unreachable, or the very first load. Both render "—";
  // fabricating a balance (or a 0) would be a lie about the org's account.
  if (!credits) {
    return (
      <div className={`px-s3 pb-s3 ${className}`}>
        <Tooltip
          className="w-full"
          content={
            unreachable
              ? 'Credit balance unavailable — the credits service didn’t respond.'
              : 'Loading credit balance…'
          }
        >
          <div
            className={`${chipBase} cursor-default border-white/[0.08] bg-white/[0.03] text-chrome-text ${
              loading && !unreachable ? 'animate-pulse' : ''
            }`}
          >
            {glyph}
            <span className="font-mono">—</span>
            <span className="text-chrome-label">credits</span>
          </div>
        </Tooltip>
      </div>
    );
  }

  const low = credits.balance < LOW_BALANCE_THRESHOLD;
  const formatted = formatCredits(credits.balance);

  return (
    <div ref={wrapperRef} className={`relative px-s3 pb-s3 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${formatted} credits${low ? ' — low balance' : ''}. Show recent activity`}
        className={`${chipBase} ${
          low
            ? 'border-warn-ink/60 bg-warn-ink/[0.18] text-warn-border hover:bg-warn-ink/[0.26]'
            : 'border-white/[0.08] bg-white/[0.03] text-chrome-text hover:bg-white/[0.07]'
        }`}
      >
        {glyph}
        <span className={`font-mono font-semibold ${low ? 'text-warn-border' : 'text-white'}`}>{formatted}</span>
        <span className={low ? 'text-warn-border/80' : 'text-chrome-label'}>
          {credits.balance === 1 ? 'credit' : 'credits'}
        </span>
        {low && (
          <span className="ml-auto text-[9.5px] font-bold uppercase tracking-[0.12em] text-warn-border">Low</span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label="Credit balance" className="absolute bottom-0 left-full z-40 ml-s3">
          <CreditsPopover credits={credits} creditRequest={creditRequest} />
        </div>
      )}
    </div>
  );
};

export default CreditsChip;
